import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../../components/ui/layout/app-header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Ticket as TicketIcon, 
  Clock, 
  CheckCircle2, 
  Eye, 
  UserCheck, 
  ShieldAlert, 
  Zap, 
  Info,
  Search,
  Filter,
  RefreshCw,
  Settings,
  UserCircle,
  LogOut,
  AlertCircle,
  MessageSquare,
  Clock4,
  XCircle
} from 'lucide-react';
import TicketDetailModal from '../../components/Ticket-detail-model';
import { format, parseISO } from 'date-fns';
import { BASE_URL } from '../../lib/api';
// API Base URL


export default function TicketsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // State management
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('current-month');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    unassigned: 0
  });

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('superadmin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Load tickets from API
  const loadTickets = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Apply filters
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.priority && filters.priority !== 'all') {
        params.append('priority', filters.priority);
      }
      if (filters.assignedTo && filters.assignedTo !== 'all') {
        params.append('assignedTo', filters.assignedTo);
      }

      const queryString = params.toString();
      const url = queryString ? 
        `${BASE_URL}/support-tickets/admin/support-tickets?${queryString}` : 
        `${BASE_URL}/support-tickets/admin/support-tickets`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform the API response to match the expected format
      const transformedTickets = data.map(ticket => ({
        id: ticket.id,
        ticketNumber: ticket.ticketNumber,
        subject: ticket.subject,
        description: ticket.description,
        status: ticket.status === 'open' ? 'Pending' : 
                ticket.status === 'in-progress' ? 'In Progress' :
                ticket.status === 'resolved' ? 'Resolved' : 
                ticket.status === 'closed' ? 'Closed' : ticket.status,
        raisedByName: ticket.userName || 'Unknown User',
        raisedByEmail: ticket.userEmail || 'Unknown Email',
        createdAt: ticket.createdAt,
        updatedAt: ticket.updatedAt,
          responses: (ticket.updates || []).map(update => ({
    id: update.id,
    responseText: update.message,
    respondedBy: update.authorName,
    respondedAt: update.createdAt,
    isInternal: update.isInternal || false
  })),  
        assignedTo: ticket.assignedTo || 'Unassigned',
        priority: ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1),
        urgency: ticket.urgency || ticket.priority,
        preferredResponseType: ticket.responseType || 'Simple',
        ticketType: ticket.ticketType === 'enhancement' ? 'Enhancement' : 'Support',
        module: ticket.module,
        resolution: ticket.resolution,
        resolvedAt: ticket.resolvedAt
      }));
      
      setTickets(transformedTickets);
      
      // Load stats if no filters
      if (Object.keys(filters).length === 0) {
        loadStats();
      }
      
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast({
        title: "Error",
        description: "Failed to load support tickets.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const response = await fetch(`${BASE_URL}/support-tickets/admin/support-tickets/stats`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Update ticket
  const updateTicket = async (ticketId, updateData) => {
    try {
      const response = await fetch(`${BASE_URL}/support-tickets/admin/support-tickets/${ticketId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Update local state
      setTickets(prevTickets =>
        prevTickets.map(t => t.id === ticketId ? {
          ...t,
          ...updateData,
          updatedAt: new Date().toISOString()
        } : t)
      );
      
      toast({
        title: "Success",
        description: "Ticket updated successfully.",
      });
      
      return data;
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast({
        title: "Error",
        description: "Failed to update ticket.",
        variant: "destructive"
      });
      throw error;
    }
  };

  // Add response to ticket
 const addResponse = async (ticketId, responseData) => {
    console.log('=== ADD RESPONSE DEBUG ===');
  console.log('Ticket ID:', ticketId);
  console.log('Response Data:', responseData);
  console.log('URL:', `${BASE_URL}/support-tickets/admin/support-tickets/${ticketId}/responses`);
  

  try {
    const response = await fetch(`${BASE_URL}/support-tickets/admin/support-tickets/${ticketId}/responses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(responseData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Update local state with proper response format
    setTickets(prevTickets =>
      prevTickets.map(t => t.id === ticketId ? {
        ...t,
        responses: [...(t.responses || []), {
          id: data.id,
          responseText: data.responseText || data.message, // Handle both formats
          respondedBy: data.respondedBy,
          respondedAt: data.respondedAt,
          isInternal: data.isInternal || false
        }],
        updatedAt: new Date().toISOString()
      } : t)
    );
    
    toast({
      title: "Success", 
      description: "Response added successfully.",
    });
    
    return data;
  } catch (error) {
    console.error('Error adding response:', error);
    toast({
      title: "Error",
      description: "Failed to add response.",
      variant: "destructive"
    });
    throw error;
  }
};

  // Filter tickets based on search and filters
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      const matchesSearch = 
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.raisedByName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.raisedByEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.ticketNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || ticket.status.toLowerCase() === filterStatus.toLowerCase();
      const matchesPriority = filterPriority === 'all' || ticket.priority.toLowerCase() === filterPriority.toLowerCase();
      const matchesAssigned = filterAssigned === 'all' || 
        (filterAssigned === 'unassigned' && (!ticket.assignedTo || ticket.assignedTo === 'Unassigned')) ||
        (filterAssigned !== 'unassigned' && ticket.assignedTo === filterAssigned);
      
      return matchesSearch && matchesStatus && matchesPriority && matchesAssigned;
    });
  }, [tickets, searchTerm, filterStatus, filterPriority, filterAssigned]);

  // Load data on component mount
  useEffect(() => {
    loadTickets();
  }, []);

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  const handleTicketUpdate = async (updatedTicket) => {
    try {
      // Update ticket in backend
      const dbStatusMap = {
        'Pending': 'open',
        'In Progress': 'in-progress', 
        'Resolved': 'resolved',
        'Closed': 'closed'
      };

      const updateData = {
        status: dbStatusMap[updatedTicket.status] || updatedTicket.status.toLowerCase(),
        priority: updatedTicket.priority.toLowerCase(),
        assignedTo: updatedTicket.assignedTo,
        resolution: updatedTicket.resolution
      };

      await updateTicket(updatedTicket.id, updateData);
      
      // Update local state with the transformed ticket
      setTickets(prevTickets =>
        prevTickets.map(t => t.id === updatedTicket.id ? updatedTicket : t)
      );
      
      // Update selected ticket if it's currently open
      if (selectedTicket && selectedTicket.id === updatedTicket.id) {
        setSelectedTicket(updatedTicket);
      }
      
      // Reload stats
      loadStats();
      
    } catch (error) {
      // Error handling is done in updateTicket function
    }
  };

  const handleAddResponse = async (ticketId, responseText, isInternal = false) => {
    try {
      await addResponse(ticketId, { responseText, isInternal });
      
      // Reload tickets to get updated data
      loadTickets();
      
    } catch (error) {
      // Error handling is done in addResponse function
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('admin_user');
    navigate('/');
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'Critical':
      case 'High':
        return 'destructive';
      case 'Medium':
        return 'secondary';
      case 'Low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-700 text-white hover:bg-red-800';
      case 'High':
        return 'bg-red-500 text-white hover:bg-red-600';
      case 'Medium':
        return 'bg-yellow-500 text-white hover:bg-yellow-600';
      case 'Low':
        return 'bg-blue-500 text-white hover:bg-blue-600';
      default:
        return 'bg-gray-500 text-white hover:bg-gray-600';
    }
  };

  const getTicketTypeBadgeClass = (type) => {
    switch (type) {
      case 'Enhancement':
        return 'bg-purple-500 text-white hover:bg-purple-600';
      case 'Bug':
        return 'bg-red-500 text-white hover:bg-red-600';
      case 'Support':
      default:
        return 'bg-gray-500 text-white hover:bg-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending':
        return <Clock4 className="h-4 w-4 text-blue-600" />;
      case 'In Progress':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'Resolved':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'Closed':
        return <XCircle className="h-4 w-4 text-gray-600" />;
      default:
        return <Clock4 className="h-4 w-4 text-gray-600" />;
    }
  };

  // Profile dropdown component
  const ProfileDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <UserCircle className="h-6 w-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">SuperAdmin</p>
            <p className="text-xs text-muted-foreground">admin@revynox.com</p>
            <p className="text-xs text-muted-foreground">Role: SuperAdmin</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Profile Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card className="shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-5 w-5 ${color || 'text-muted-foreground'}`} />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );

  return (
    <>
      <AppHeader title="Ticket Management">
        <div className="flex items-center gap-4">
          <ProfileDropdown />
        </div>
      </AppHeader>

      <main className="flex-1 p-4 md:p-6 space-y-6">
        {/* Period Selector */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Ticket Overview</h2>
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current-month">Current Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="last-3-months">Last 3 Months</SelectItem>
              <SelectItem value="last-year">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-5">
          <StatCard title="Total Tickets" value={stats.total} icon={TicketIcon} color="text-primary" />
          <StatCard title="Pending Tickets" value={stats.pending} icon={Clock} color="text-accent" />
          <StatCard title="In Progress" value={stats.inProgress} icon={RefreshCw} color="text-blue-600" />
          <StatCard title="Resolved Tickets" value={stats.resolved} icon={CheckCircle2} color="text-green-600" />
          <StatCard title="Unassigned" value={stats.unassigned} icon={ShieldAlert} color="text-red-600" />
        </div>

        {/* Filters and Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <input
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-md text-sm w-full"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterAssigned} onValueChange={setFilterAssigned}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Assignment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignments</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="Alice Admin">Alice Admin</SelectItem>
                  <SelectItem value="Bob Support">Bob Support</SelectItem>
                  <SelectItem value="Charlie Tech">Charlie Tech</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                className="flex items-center"
                onClick={() => loadTickets()}
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tickets Table */}
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-headline">All Tickets</CardTitle>
                <CardDescription>Click on a ticket to view details and respond.</CardDescription>
              </div>
              <div className="text-sm text-muted-foreground">
                Showing {filteredTickets.length} of {tickets.length} tickets
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-600">Loading tickets...</span>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket #</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Raised By</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTickets.length > 0 ? filteredTickets.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                        className="hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => handleViewTicket(ticket)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(ticket.status)}
                            <span>#{ticket.ticketNumber || ticket.id}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium max-w-xs">
                          <div className="truncate">{ticket.subject}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <MessageSquare className="h-3 w-3" />
                            {ticket.responses?.length || 0} responses
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>{ticket.raisedByName}</div>
                          <div className="text-xs text-muted-foreground">{ticket.raisedByEmail}</div>
                        </TableCell>
                        <TableCell>
                          {ticket.assignedTo && ticket.assignedTo !== 'Unassigned' ? (
                            <span className="flex items-center text-sm">
                              <UserCheck className="mr-1.5 h-4 w-4 text-green-600"/> {ticket.assignedTo}
                            </span>
                          ) : (
                            <span className="flex items-center text-sm text-muted-foreground">
                              <ShieldAlert className="mr-1.5 h-4 w-4 text-orange-500"/> Unassigned
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPriorityBadgeVariant(ticket.priority)} className={getPriorityBadgeClass(ticket.priority)}>
                            {ticket.priority === 'Critical' && <AlertCircle className="mr-1 h-3 w-3" />}
                            {ticket.priority || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={ticket.status === 'Resolved' ? 'default' : 'secondary'}
                            className={ticket.status === 'Resolved' ? 'bg-green-600 hover:bg-green-700 text-white' : 
                              ticket.status === 'In Progress' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                              'bg-yellow-600 hover:bg-yellow-700 text-white'}>
                            {ticket.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getTicketTypeBadgeClass(ticket.ticketType)}>
                            {ticket.ticketType === 'Enhancement' ? <Zap className="mr-1 h-3 w-3"/> : <Info className="mr-1 h-3 w-3"/>}
                            {ticket.ticketType || 'Support'}
                          </Badge>
                        </TableCell>
                        <TableCell>{format(parseISO(ticket.updatedAt), 'MMM d, yyyy HH:mm')}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleViewTicket(ticket); }}>
                            <Eye className="mr-2 h-4 w-4" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center h-24">
                          No tickets found matching your criteria.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {selectedTicket && (
          <TicketDetailModal
            ticket={selectedTicket}
            isOpen={isModalOpen}
            onClose={handleModalClose}
            onTicketUpdate={handleTicketUpdate}
            onAddResponse={handleAddResponse}
          />
        )}
      </main>
    </>
  );
}